// src/features/audittrail/GenerateReportDialog.tsx
// Generate a compliance report for a regulator — asynchronous, a Kafka event
// fires when it's ready; GetComplianceReport/polling shows the status.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Input, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import type { ComplianceReportType } from '@core/types/audittrail';
import { AudittrailModal } from './Modal';
import { COMPLIANCE_REPORT_TYPE_VALUES, enumKey, errMessage } from './logic';

interface GenerateReportDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerated: () => void;
}

export const GenerateReportDialog = observer(({ open, onClose, onGenerated }: GenerateReportDialogProps) => {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [reportType, setReportType] = useState<ComplianceReportType>('COMPLIANCE_REPORT_TYPE_ACCESS_LOG');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim() || !periodStart || !periodEnd) return;
    setSaving(true);
    setError(null);
    try {
      await audittrailStore.GenerateComplianceReport({
        name: name.trim(),
        report_type: reportType,
        period_start: new Date(periodStart).toISOString(),
        period_end: new Date(periodEnd).toISOString(),
      });
      setName(''); setPeriodStart(''); setPeriodEnd('');
      onGenerated();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('audittrail.compliance.form.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AudittrailModal
      open={open}
      title={t('audittrail.compliance.form.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={submit} isLoading={saving} disabled={!name.trim() || !periodStart || !periodEnd}>{t('audittrail.compliance.form.submit')}</Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <ErrorAlert error={error} />}
        <Input label={t('audittrail.compliance.form.name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Select
          label={t('audittrail.compliance.form.type')}
          value={reportType}
          onChange={(e) => setReportType(e.target.value as ComplianceReportType)}
          options={COMPLIANCE_REPORT_TYPE_VALUES.map((v) => ({ value: v, label: t(`audittrail.complianceReportType.${enumKey(v, 'COMPLIANCE_REPORT_TYPE_')}`) }))}
        />
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" label={t('audittrail.compliance.form.periodStart')} value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} required />
          <Input type="date" label={t('audittrail.compliance.form.periodEnd')} value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} required />
        </div>
      </div>
    </AudittrailModal>
  );
});

export default GenerateReportDialog;
